// src/components/FormFiller.jsx
import React, { useState, useEffect } from 'react';
import './FormFiller.css';
import ImageUpload from './ImageUpload';
import config from '../config';

const FormFiller = ({ template, onBack }) => {
  const costPerPdf = parseFloat(config.COST_PER_PDF)
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [touchedFields, setTouchedFields] = useState({});
  const [csrfToken, setCsrfToken] = useState('');
  const [uploadedImages, setUploadedImages] = useState({}); // Add state for images
  const [resetKey, setResetKey] = useState(0);
  const [pdfLimitError, setPdfLimitError] = useState('');
  const [showPdfUpgradeModal, setShowPdfUpgradeModal] = useState(false);

  // Fetch CSRF token on component mount
  useEffect(() => {
    fetchCSRFToken();
  }, []);

  // Calculate form progress
  useEffect(() => {
    if (!template?.sections) return;

    const totalFields = template.sections.reduce(
      (total, section) => total + (section.fields?.length || 0),
      0
    );

    const filledFields = Object.keys(formData).filter(key =>
      formData[key] &&
      (Array.isArray(formData[key]) ? formData[key].length > 0 : true)
    ).length;

    setProgress(totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0);
  }, [formData, template]);


  // Check PDF limit before submission
  const checkPdfLimit = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/analytics/`, {
        credentials: 'include'
      });

      if (!response.ok) {
        return true; // Allow submission if can't check
      }

      const data = await response.json();

      // For free plan users, check if they've reached PDF limit
      const pdfsUsed = data.profile.free_pdfs_generated;
      const pdfLimit = data.profile.free_pdf_limit;
      const accBalance = data.profile.balance;
      const pdfRate = data.profile.pdf_generation_rate;
      if (data.profile.plan === 'free') {
        if (pdfsUsed >= pdfLimit) {
          const errorMsg = `You have reached your free plan limit of ${pdfLimit} PDFs. Please upgrade to continue generating PDFs.`;
          setPdfLimitError(errorMsg);
          setShowPdfUpgradeModal(true);
          return false;
        }
      }
      else if (data.profile.plan === 'usage') {
        if (pdfsUsed >= pdfLimit) {
          if (accBalance <= pdfRate) {
            const errorMsg = `⚠️ Low balance! Please recharge to generate PDFs. Your balance is ${accBalance}.`;
            setPdfLimitError(errorMsg);
            setShowPdfUpgradeModal(true);
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      // console.error('Error checking PDF limit:', error);
      return true; // Allow submission on error
    }
  };


  // Add handler for image uploads
  const handleImageUpload = (fieldId, images) => {
    // console.log(`Uploading ${images.length} images for field ${fieldId}`);
    setUploadedImages(prev => ({
      ...prev,
      [fieldId]: images
    }));
  };

  // Add this function to validate image fields
  const validateImageField = (field, images) => {
    let error = null;

    if (field.required && (!images || images.length === 0)) {
      error = 'Please upload at least one image';
    }

    // Optional: Add file size validation
    if (images && images.length > 0) {
      const maxSize = 10 * 1024 * 1024; // 5MB
      const oversized = images.filter(img => img.size > maxSize);
      if (oversized.length > 0) {
        error = `Some images exceed the 5MB size limit`;
      }
    }

    setErrors(prev => ({
      ...prev,
      [field.id]: error
    }));

    return !error;
  };

  // Add render function for image field
  const renderImageField = (field) => {
    const error = errors[field.id];
    const isTouched = touchedFields[field.id];
    const showError = isTouched && error;
    const hasImages = uploadedImages[field.id] && uploadedImages[field.id].length > 0;

    return (
      <div className="form-field">
        {/* <label className="field-label">
          {field.label}
          {field.required && <span className="required-star">*</span>}
        </label> */}
        <ImageUpload
          key={`${field.id}-${resetKey}`}  // Add this line
          field={field}
          onImagesChange={(images) => {
            handleImageUpload(field.id, images);
            // Mark as touched and validate
            setTouchedFields(prev => ({ ...prev, [field.id]: true }));
            validateImageField(field, images);
          }}
        />
        {field.required && !hasImages && isTouched && (
          <div className="field-error">Please upload at least one image</div>
        )}
        {showError && <div className="field-error">{error}</div>}
        <div className="helper-text">
          Upload multiple images. Supported: JPG, PNG, GIF
        </div>
      </div>
    );
  };

  // Helper function to get cookie value
  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  // Fetch CSRF token from server
  const fetchCSRFToken = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/auth/csrf/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const token = getCookie('csrftoken');
        setCsrfToken(token);
        // console.log('CSRF Token fetched:', token);
      }
    } catch (error) {
      // console.error('Error fetching CSRF token:', error);
    }
  };

  const validateField = (field, value) => {
    if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
      return 'This field is required';
    }

    if (field.validation_criteria) {
      const criteria = field.validation_criteria;

      if (field.field_type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
      }

      if (field.field_type === 'number' && value) {
        if (criteria.min !== undefined && value < criteria.min) {
          return `Minimum value is ${criteria.min}`;
        }
        if (criteria.max !== undefined && value > criteria.max) {
          return `Maximum value is ${criteria.max}`;
        }
      }

      if (field.field_type === 'text' && value) {
        if (criteria.min_length && value.length < criteria.min_length) {
          return `Minimum length is ${criteria.min_length} characters`;
        }
        if (criteria.max_length && value.length > criteria.max_length) {
          return `Maximum length is ${criteria.max_length} characters`;
        }
      }
    }

    return null;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field.id]: value
    }));

    setTouchedFields(prev => ({
      ...prev,
      [field.id]: true
    }));

    const error = validateField(field, value);
    setErrors(prev => ({
      ...prev,
      [field.id]: error
    }));
  };

  const handleBlur = (field) => {
    setTouchedFields(prev => ({
      ...prev,
      [field.id]: true
    }));

    if (field.field_type === 'image') {
      const images = uploadedImages[field.id] || [];
      validateImageField(field, images);
    } else {
      const error = validateField(field, formData[field.id]);
      setErrors(prev => ({
        ...prev,
        [field.id]: error
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    template.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.field_type === 'image') {
          // Validate image field
          const images = uploadedImages[field.id] || [];

          if (field.required && images.length === 0) {
            newErrors[field.id] = 'Please upload at least one image';
            isValid = false;
          }
        } else {
          // Validate text fields
          const error = validateField(field, formData[field.id]);
          if (error) {
            newErrors[field.id] = error;
            isValid = false;
          }
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitSuccess(false);

    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Check PDF limit BEFORE submission
    const canProceed = await checkPdfLimit();
    if (!canProceed) {
      return; // Stop submission if limit reached
    }

    setIsSubmitting(true);

    try {
      // Ensure we have a CSRF token
      let token = csrfToken || getCookie('csrftoken');
      // if (!token) {
      //   await fetchCSRFToken();
      //   token = getCookie('csrftoken');
      // }
      try {
        await fetchCSRFToken();
        token = getCookie('csrftoken');
      } catch (e) {
        {
          !token && (
            <div className="warning-message">
              <span>⚠️</span>
              <span>CSRF token not loaded. Trying to fetch...</span>
            </div>
          )
        }
        // console.error('Getting error for csrftoken:', e);
      }

      // console.log('Submitting form data:', formData);
      // console.log('Template ID:', template.id);
      // console.log('CSRF Token:', token);

      // Create FormData object
      const formDataObj = new FormData();
      formDataObj.append('form_template_id', template.id);
      formDataObj.append('response_data', JSON.stringify(formData));

      // Add images
      for (const [fieldId, images] of Object.entries(uploadedImages)) {
        images.forEach((image, index) => {
          formDataObj.append(`images_${fieldId}_${index}`, image);
        });
      }

      // Debug: Log FormData contents
      // console.log('Submitting FormData:');
      for (let pair of formDataObj.entries()) {
        if (pair[1] instanceof File) {
          console.log(pair[0], pair[1].name, pair[1].type, pair[1].size);
        } else {
          console.log(pair[0], pair[1]);
        }
      }

      const response = await fetch(`${config.API_BASE_URL}/api/public/form-responses/`, {
        method: 'POST',
        headers: {
          // 'Content-Type': 'application/json',
          'X-CSRFToken': token
        },
        credentials: 'include',
        body: formDataObj
      });

      // console.log('Response status:', response.status);
      // console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Get response text
      const responseText = await response.text();
      // console.log('Raw response:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
      }

      if (!response.ok) {
        if (response.status === 403) {
          if (responseText.includes('CSRF') || responseText.includes('csrf')) {
            // Try to refresh CSRF token and retry
            await fetchCSRFToken();
            throw new Error('CSRF token expired. Please try submitting again.');
          } else {
            throw new Error('Permission denied. You may need to log in.');
          }
        } else if (response.status === 400) {
          const errorMessages = [];
          if (responseData) {
            Object.keys(responseData).forEach(key => {
              if (Array.isArray(responseData[key])) {
                errorMessages.push(`${key}: ${responseData[key].join(', ')}`);
              } else {
                errorMessages.push(`${key}: ${responseData[key]}`);
              }
            });
          }
          throw new Error(errorMessages.length > 0 ? errorMessages.join('\n') : 'Invalid form data');
        } else {
          throw new Error(responseData?.message || `Server error: ${response.status}`);
        }
      }

      // console.log('Form submitted successfully:', responseData);

      // Download PDF
      try {
        const pdfResponse = await fetch(
          `${config.API_BASE_URL}/api/form-responses/${responseData.id}/download_pdf/`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-CSRFToken': token,
            }
          }
        );

        // console.log('PDF response status:', pdfResponse.status);

        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `form_${template.name.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          console.error('PDF download failed:', pdfResponse.status);
        }
      } catch (pdfError) {
        console.error('Error downloading PDF:', pdfError);
      }

      setSubmitSuccess(true);
      setFormData({});
      setUploadedImages({});
      setTouchedFields({});
      setErrors({});

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);

    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all fields?')) {
      setFormData({});
      setErrors({});
      setTouchedFields({});
      setUploadedImages({});
      setResetKey(prev => prev + 1); // Add this line
      setError('');
    }
  };

  const renderField = (field) => {
    const value = formData[field.id] || '';
    const error = errors[field.id];
    const isTouched = touchedFields[field.id];
    const showError = isTouched && error;

    const commonProps = {
      id: `field-${field.id}`,
      className: `field-input ${showError ? 'error' : ''}`,
      onBlur: () => handleBlur(field),
      required: field.required,
      disabled: isSubmitting
    };

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'number':
      case 'date':
        return (
          <input
            type={field.field_type}
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            {...commonProps}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className={`field-textarea ${showError ? 'error' : ''}`}
            onBlur={() => handleBlur(field)}
            id={`field-${field.id}`}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            rows={4}
            required={field.required}
            disabled={isSubmitting}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className={`field-select ${showError ? 'error' : ''}`}
            onBlur={() => handleBlur(field)}
            id={`field-${field.id}`}
            required={field.required}
            disabled={isSubmitting}
          >
            <option value="">Select an option</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="radio-group">
            {field.options?.map((option, index) => (
              <label key={index} className="radio-option">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  onBlur={() => handleBlur(field)}
                  required={field.required && !value}
                  disabled={isSubmitting}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        const currentValues = Array.isArray(value) ? value : [];
        return (
          <div className="checkbox-group">
            {field.options?.map((option, index) => (
              <label key={index} className="checkbox-option">
                <input
                  type="checkbox"
                  value={option}
                  checked={currentValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter(val => val !== option);
                    handleInputChange(field, newValues);
                  }}
                  onBlur={() => handleBlur(field)}
                  disabled={isSubmitting}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'image':
        // console.info("render images function")
        return renderImageField(field);

      default:
        return null;
    }
  };

  if (!template) {
    return (
      <div className="form-filler-container">
        <div className="error-message">No template selected</div>
        <button onClick={onBack} className="back-button">← Back</button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* PDF Limit Upgrade Modal */}
      {showPdfUpgradeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <span className="modal-icon">⚠️</span>
              <h2>PDF Generation Limit Reached</h2>
              <button className="modal-close" onClick={() => setShowPdfUpgradeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>{pdfLimitError}</p>
              <div className="modal-options">
                <div className="option-card">
                  <h3>📊 Free Plan</h3>
                  <p>Your current plan</p>
                  <ul>
                    <li>✓ 2 Templates (max)</li>
                    <li>✓ 5 PDFs lifetime</li>
                    <li>✓ Basic form fields</li>
                  </ul>
                </div>
                <div className="option-card recommended">
                  <div className="recommended-badge">Recommended</div>
                  <h3>🚀 Usage Plan</h3>
                  <p>Unlimited PDF generation</p>
                  <ul>
                    <li>✓ Unlimited templates</li>
                    <li>✓ ${costPerPdf} per PDF generation</li>
                    <li>✓ Image upload support</li>
                    <li>✓ Priority support</li>
                  </ul>
                  <button
                    onClick={() => window.location.href = '/analytics'}
                    className="upgrade-modal-btn"
                  >
                    Upgrade Now →
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowPdfUpgradeModal(false)} className="cancel-btn">Cancel</button>
              <button onClick={() => window.location.href = '/analytics'} className="upgrade-footer-btn">
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}
      <main className="dashboard-main">
        <div className="form-filler-container">
          <button onClick={onBack} className="back-button" disabled={isSubmitting}>
            <span className="back-icon">←</span>
            Back to Templates
          </button>
          <div className="form-filler-header">
            <h1 className="form-title">{template.name}</h1>
          </div>

          <div className="form-progress">
            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
            <div className="progress-text">{progress}% Complete</div>
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <div className="error-content">
                <strong>Error:</strong>
                <p>{error}</p>
              </div>
              <button className="error-close" onClick={() => setError('')}>×</button>
            </div>
          )}

          {submitSuccess && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              <div className="success-content">
                <strong>Success!</strong>
                <p>Form submitted successfully! PDF download started.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {template.sections?.map(section => (
              <div key={section.id} className="section-card">
                <div className="section-header">
                  <h2 className="section-title">
                    <span className="section-icon">📋</span>
                    {section.name}
                  </h2>
                  <span className="section-field-count">
                    {section.fields?.length || 0} field(s)
                  </span>
                </div>

                {section.fields?.map(field => (
                  <div key={field.id} className="form-field">
                    <label className="field-label" htmlFor={`field-${field.id}`}>
                      {field.label}
                      {field.required && <span className="required-star">*</span>}
                    </label>

                    {renderField(field)}

                    {errors[field.id] && touchedFields[field.id] && (
                      <div className="field-error">
                        <span>⚠️</span>
                        {errors[field.id]}
                      </div>
                    )}

                    {field.validation_criteria?.max_length && (
                      <div className="helper-text">
                        Max {field.validation_criteria.max_length} characters
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            <div className="form-actions">
              <button
                type="submit"
                className={`submit-button ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit & Download PDF'
                )}
              </button>

              <button
                type="button"
                className="reset-button"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                Reset Form
              </button>
            </div>
          </form>

        </div>
      </main>
    </div>
  );
};

export default FormFiller;